import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  allowsUnitKind,
  FLOOR_PLAN_KIND_LABEL,
  type FloorPlanKind,
} from '@obraplan/core/domain/takeoff/floor-plan';
import type { ProjectTakeoff } from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import type { UnitTypeKind } from '@obraplan/core/domain/takeoff/unit-type';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { NomeInput } from '@/features/takeoff/campos';
import {
  ehAreaUnica,
  espinhaNomes,
  nomeDaAreaUnica,
  nomeDaTipologia,
  pecasDaTipologia,
  rotuloDoTipo,
  temAreaUnica,
} from '@/features/takeoff/area-unica';
import {
  activeTypology,
  floorsOfDrawing,
  typologiesOfDrawing,
  type TypologyDraft,
  type WorkspaceAction,
  type WorkspaceDraft,
} from '@/features/takeoff/typology-draft';

type Dispatch = React.Dispatch<WorkspaceAction>;

/**
 * As escritas que a espinha do quantitativo faz, num objeto só.
 *
 * São seis casos de uso e todos precisam da obra e da sessão. Passá-los soltos daria seis
 * props a cada cartão; agrupados, o cartão declara o que faz e não como chega lá.
 */
export interface AcoesDaEspinha {
  readonly ligarPavimento: (floorId: string, floorPlanId?: string) => void;
  /** A mesma ligação para uma faixa de andares — o tipo, do 1º ao 12º, de uma vez. */
  readonly ligarPavimentos: (floorIds: readonly string[], floorPlanId?: string) => void;
  readonly criarDesenho: (name: string, kind: FloorPlanKind) => void;
  readonly removerDesenho: (floorPlanId: string) => void;
  readonly criarTipologia: (input: {
    name: string;
    kind: UnitTypeKind;
    floorPlanId: string;
    /** Quantas cabem no pavimento. Ausente é uma — o apto e a área comum. */
    positionCount?: number;
  }) => void;
  /** `true` quando o servidor aceitou o nome; recusado, o toast já disse por quê. */
  readonly renomearTipologia: (unitTypeId: string, name: string) => Promise<boolean>;
  readonly removerTipologia: (unitTypeId: string) => void;
}

/**
 * Um desenho e as tipologias dele.
 *
 * O agrupamento é o assunto: o garden não é um apartamento a mais no pavimento tipo, é
 * outro andar. Ver as tipologias soltas numa fila só escondia isso — e escondia também
 * que cada grupo se multiplica por um número diferente.
 */
export function PavimentoBloco({
  plano,
  workspace,
  dispatch,
  acoes,
}: {
  plano: ProjectTakeoff['floorPlans'][number];
  workspace: WorkspaceDraft;
  dispatch: Dispatch;
  acoes: AcoesDaEspinha;
}) {
  const ativa = activeTypology(workspace);
  // Pelo desenho, e não pelo tipo: dois desenhos de "Pavimento tipo" têm tipologias e
  // pavimentos próprios.
  const doDesenho = { floorKind: plano.kind, floorPlanId: plano.id };
  const daqui = typologiesOfDrawing(workspace, doDesenho);
  const quantos = floorsOfDrawing(workspace, doDesenho);
  const unica = temAreaUnica(plano.kind);
  const temAreaUnicaCriada = daqui.some(ehAreaUnica);

  return (
    <section className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {plano.name}{' '}
          <span className="font-normal text-(--color-content-muted)">
            {FLOOR_PLAN_KIND_LABEL[plano.kind]}
          </span>
        </h3>
        <span className="text-xs text-(--color-content-muted)">
          {quantos === 0
            ? 'nenhum pavimento apontando — o desenho conta uma vez'
            : `${String(quantos)} ${quantos === 1 ? 'pavimento' : 'pavimentos'} na obra`}
          {!unica &&
            daqui.length > 0 &&
            ` · ${String(daqui.length)} ${daqui.length === 1 ? 'tipologia' : 'tipologias'}`}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {daqui.map((typology) => (
          <TipologiaCard
            key={typology.unitTypeId}
            typology={typology}
            selecionada={typology.unitTypeId === ativa.unitTypeId}
            dispatch={dispatch}
            acoes={acoes}
            {...(unica && !ehAreaUnica(typology)
              ? {
                  aviso: `${rotuloDoTipo(typology)} não pertence à ${FLOOR_PLAN_KIND_LABEL[plano.kind]}`,
                }
              : {})}
          />
        ))}

        {unica ? (
          // Fundação e Contenção têm uma área só, a da base do prédio: sem apto, sem vaga e
          // sem uma segunda área comum. O que já existia de outro tipo (obra antiga) aparece
          // com aviso e o botão de remover de sempre — a tela não apaga nada sozinha.
          !temAreaUnicaCriada && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                acoes.criarTipologia({
                  name: nomeDaAreaUnica(plano.name, espinhaNomes(workspace)),
                  kind: 'AREA_COMUM',
                  floorPlanId: plano.id,
                })
              }
            >
              <Plus className="size-4" /> Criar a área única
            </Button>
          )
        ) : (
          <>
            {/*
              Três portas, porque são três coisas, e cada pavimento abre as suas. O hall do
              térreo e a circulação do tipo são ambos "área comum", e cada um mora no seu
              pavimento; a garagem não tem apto e tem vaga; o tipo tem apto e não tem vaga.
              Quem decide é o domínio (`allowsUnitKind`), e não uma segunda lista aqui — a
              tela esconder é conveniência, e o caso de uso continua recusando (RN-134).
            */}
            {allowsUnitKind(plano.kind, 'APARTAMENTO') && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  acoes.criarTipologia({
                    name: proximoNome(espinhaNomes(workspace), 'APARTAMENTO'),
                    kind: 'APARTAMENTO',
                    floorPlanId: plano.id,
                  })
                }
              >
                <Plus className="size-4" /> Apto
              </Button>
            )}
            {allowsUnitKind(plano.kind, 'VAGA') && (
              <NovaVaga plano={plano} workspace={workspace} acoes={acoes} />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                acoes.criarTipologia({
                  name: proximoNome(espinhaNomes(workspace), 'AREA_COMUM'),
                  kind: 'AREA_COMUM',
                  floorPlanId: plano.id,
                })
              }
            >
              <Plus className="size-4" /> Área comum
            </Button>
          </>
        )}
      </div>

      {unica && (
        <p className="text-xs text-(--color-content-muted)">
          Área única da {FLOOR_PLAN_KIND_LABEL[plano.kind].toLowerCase()} — estacas, blocos,
          baldrames, radier e contenção moram aqui.
        </p>
      )}
    </section>
  );
}

/**
 * A vaga, com o número dela.
 *
 * É a única tipologia que nasce dizendo **quantas** — o apto e a área comum existem uma
 * por posição, porque a divisa entre o 01 e o 02 precisa ser de alguém (RN-163). Vaga é
 * vaga: quarenta iguais são um cadastro vezes quarenta, e o total sai daí multiplicado
 * pelos pavimentos de garagem que a estrutura tiver.
 */
function NovaVaga({
  plano,
  workspace,
  acoes,
}: {
  plano: ProjectTakeoff['floorPlans'][number];
  workspace: WorkspaceDraft;
  acoes: AcoesDaEspinha;
}) {
  const [quantas, setQuantas] = useState('1');
  const numero = Number.parseInt(quantas, 10);
  const valido = Number.isInteger(numero) && numero >= 1 && numero <= 1000;

  return (
    <span className="flex items-end gap-1">
      <Field label="Vagas" htmlFor={`vagas-${plano.id}`}>
        <Input
          id={`vagas-${plano.id}`}
          inputMode="numeric"
          value={quantas}
          aria-invalid={!valido}
          className="w-20"
          onChange={(event) => setQuantas(event.target.value)}
        />
      </Field>
      <Button
        variant="secondary"
        size="sm"
        disabled={!valido}
        onClick={() => {
          acoes.criarTipologia({
            name: proximoNome(espinhaNomes(workspace), 'VAGA'),
            kind: 'VAGA',
            floorPlanId: plano.id,
            positionCount: numero,
          });
          setQuantas('1');
        }}
      >
        <Plus className="size-4" /> Vaga
      </Button>
    </span>
  );
}

/**
 * Uma tipologia, com o que se faz com ela.
 *
 * Renomear e remover moram **aqui**, e não num campo solto no fim do cartão. O campo solto
 * agia sempre sobre "a tipologia na mesa", que é um estado que não se vê: quem tinha três
 * tipologias no pavimento precisava selecionar uma, descer até o campo e confiar que era
 * daquela que ele falava. Com os botões no próprio cartão, o alvo é o que está debaixo do
 * dedo.
 *
 * Selecionar é um botão, e os dois ícones são outros dois ao lado — e não botões dentro
 * de um botão, que o HTML não permite e o leitor de tela não sabe anunciar.
 */
export function TipologiaCard({
  typology,
  selecionada,
  dispatch,
  acoes,
  aviso,
}: {
  typology: TypologyDraft;
  selecionada: boolean;
  dispatch: Dispatch;
  acoes: AcoesDaEspinha;
  /** Por que esta tipologia destoa do desenho dela — a tela avisa, e quem decide é a pessoa. */
  aviso?: string;
}) {
  const nome = nomeDaTipologia(typology);
  const pecas = ehAreaUnica(typology) ? pecasDaTipologia(typology) : 0;
  const [editando, setEditando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  /**
   * A gravação do nome está a caminho, ou foi recusada: sair do campo não fecha a edição.
   * Recusado — nome repetido, nome vazio —, o campo fica aberto com o toast dizendo por quê;
   * o nome voltava calado e a pessoa não sabia se tinha pegado.
   */
  const segurando = useRef(false);

  return (
    <div
      className={cn(
        'flex items-center gap-0.5 rounded-md border pr-1',
        selecionada
          ? 'border-(--color-brand) bg-(--color-surface-muted)'
          : 'border-(--color-border-subtle)',
      )}
    >
      {editando ? (
        <NomeInput
          autoFocus
          aria-label={`Nome de ${nome}`}
          value={typology.name}
          className="m-1 h-9 w-44 text-sm"
          onCommit={(name) => {
            segurando.current = true;
            void acoes.renomearTipologia(typology.unitTypeId, name).then((aceito) => {
              segurando.current = false;
              if (aceito) setEditando(false);
            });
          }}
          onDescartar={() => {
            // O `onBlur` vem logo depois, no mesmo evento: segura só até ele passar.
            segurando.current = true;
            setTimeout(() => {
              segurando.current = false;
            }, 0);
            toast.error('O nome da tipologia é obrigatório.');
          }}
          onKeyDown={(evento) => {
            if (evento.key === 'Escape') {
              segurando.current = false;
              setEditando(false);
            }
          }}
          // Sai do modo de edição mesmo quando nada mudou: `onCommit` só dispara quando há
          // o que gravar, e sozinho deixaria o cartão preso no campo.
          onBlur={() => {
            if (segurando.current) return;
            setEditando(false);
          }}
        />
      ) : (
        <button
          type="button"
          aria-current={selecionada ? 'true' : undefined}
          onClick={() => dispatch({ type: 'SELECT_TYPOLOGY', unitTypeId: typology.unitTypeId })}
          className={cn(
            'flex flex-col items-start gap-0.5 rounded-l-md px-3 py-2 text-left text-sm',
            selecionada ? 'font-medium' : 'hover:bg-(--color-surface-muted)',
          )}
        >
          <span>{nome}</span>
          <span className="text-xs text-(--color-content-muted)">
            {rotuloDoTipo(typology)}
            {/* Quantas cabem no pavimento só é notícia quando não é uma. */}
            {(typology.unitsPerFloor ?? 1) > 1 &&
              ` · ${String(typology.unitsPerFloor)} no pavimento`}{' '}
            · {typology.rooms.length} {typology.rooms.length === 1 ? 'ambiente' : 'ambientes'}
          </span>
          {aviso !== undefined && (
            <span className="text-xs font-medium text-(--color-state-warn)">{aviso}</span>
          )}
        </button>
      )}

      <Button
        variant="ghost"
        size="icon"
        aria-label={`Renomear ${nome}`}
        onClick={() => setEditando(true)}
      >
        <Pencil className="size-4" aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Remover ${nome}`}
        onClick={() => setRemovendo(true)}
      >
        <Trash2 className="size-4" aria-hidden="true" />
      </Button>

      {/*
        Confirmação porque agora há um destes por cartão. O clique errado custa o mesmo de
        antes — sai a tipologia e sai com ela toda a geometria montada —, e agora há mais
        alvos para errar.
      */}
      <ConfirmDialog
        open={removendo}
        onOpenChange={setRemovendo}
        title={`Remover ${nome}?`}
        description={
          pecas > 0
            ? `Saem junto ${String(pecas)} ${pecas === 1 ? 'peça' : 'peças'} e tudo o que foi lançado nela. Não há como desfazer.`
            : 'Saem junto os ambientes, as paredes e o que foi lançado nela. Não há como desfazer.'
        }
        confirmLabel="Remover"
        destructive
        onConfirm={() => {
          acoes.removerTipologia(typology.unitTypeId);
          setRemovendo(false);
        }}
      />
    </div>
  );
}

/**
 * O próximo nome livre da obra.
 *
 * O nome é a identidade da tipologia dentro da obra, e o servidor recusa repetido: sem
 * isto, o segundo "Apartamento 1" viraria um erro em vez de um cadastro. Conta as que já
 * existem em vez de perguntar — é derivado, e derivado não desencontra.
 */
const BASE_DO_NOME: Readonly<Record<UnitTypeKind, string>> = {
  APARTAMENTO: 'Apartamento',
  VAGA: 'Vaga',
  AREA_COMUM: 'Área comum',
};

function proximoNome(usados: ReadonlySet<string>, kind: UnitTypeKind): string {
  const base = BASE_DO_NOME[kind];
  for (let n = 1; n < 100; n += 1) {
    const nome = `${base} ${String(n)}`;
    if (!usados.has(nome)) return nome;
  }
  return `${base} ${String(usados.size + 1)}`;
}
